import { Injectable } from '@angular/core';
import { LemsarApiClient } from '@lemsar/api/client';
import { UI } from '@shared/models/ui';
import { Observable } from 'rxjs/Observable';
import { StoreData } from '@shared/module/common/model/store-data';
import { EligibleInstitution, Institution } from '@lemsar/institution/domains/model/institution';
import { ResponseTransformer } from '@lemsar/api/transformers/response';
import { Collection } from '@lemsar/common/domains/interface/collection';
import { Document } from '@lemsar/institution/domains/model/document';
import { SignatureFileJkp } from '@lemsar/institution/domains/model/signature-file-jkp';
import { InstitutionProgram, Origin } from '@lemsar/institution/domains/model/institution-program';
import { AddProgramJkp } from '@lemsar/institution/domains/model/add-program-jkp';
import { User } from '@naker/naco';
import { DocumentJkp } from '@lemsar/institution/domains/model/document-jkp';
import { UpdateJkpInstitution } from '@lemsar/institution/domains/model/update-jkp-institution';
import { Bank } from '@lemsar/institution/domains/model/bank';
import { CurateJkp } from '@lemsar/institution/domains/model/curate-jkp';
import { PkbDocumentPrivate } from '@lemsar/institution/domains/model/pkb-document-private';
import { ApproveSignPkb } from '@lemsar/institution/domains/model/approve-sign-pkb';

@Injectable()
export class JkpInstitutionService {
    private _ui: StoreData<UI>;

    public constructor(private api: LemsarApiClient) {
        this._ui = new StoreData<UI>(new UI());
    }

    public get ui$(): Observable<UI> {
        return this._ui.stream$;
    }

    public get ui(): UI {
        return this._ui.value;
    }

    public checkEligibleInstitution(institution: string): Observable<EligibleInstitution> {
        return this.api
            .get<any>(`institutions/${institution}/check-eligible`)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(EligibleInstitution));
    }

    public fetchJkpDocumentInstitution(institution: string, params?: any): Observable<Collection<Document>> {
        return this.api.get<any>(`institutions/${institution}/jkp-documents`, params).pipe(ResponseTransformer.toCollection(Document));
    }

    public fetchJkpProgramsInstitution(institution: string, params?: any): Observable<Collection<InstitutionProgram>> {
        return this.api
            .get<any>(`institutions/${institution}/categories/jkp`, params)
            .pipe(ResponseTransformer.toCollection(InstitutionProgram));
    }

    public fetchAvailableProgramsInstitution(params?: any): Observable<Collection<Origin>> {
        return this.api.get<any>(`public-jkp-programs`, params).pipe(ResponseTransformer.toCollection(Origin));
    }

    public fetchProgramsInstitution(institution: string, params?: any): Observable<Collection<Origin>> {
        return this.api.get<any>(`institutions/${institution}/jkp-programs`, params).pipe(ResponseTransformer.toCollection(Origin));
    }

    public createJkpInstitution(institution: string): Observable<Institution> {
        return this.api
            .post<any>(`institutions/${institution}/create-jkp`, {})
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public proposeJkpInstitution(institution: string): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/propose-jkp`, {})
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public verifyJkpInstitution(institution: string): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/verify-jkp`, {})
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public rejectJkpInstitution(institution: string, payload: any): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/reject-jkp`, payload)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public applyJkpInstitution(institution: string): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/apply-jkp`, {})
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public approveJkpInstitution(institution: string): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/approve-jkp`, {})
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public curateJkpInstitution(institution: string, payload: CurateJkp): Observable<Institution> {
        const formData = new FormData();
        formData.append('pkbFile', payload.pkbFile);

        return this.api
            .post<any>(`institutions/${institution}/curate-jkp`, formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public signatureJkpFile(institution: string, payload: SignatureFileJkp): Observable<Institution> {
        const formData = new FormData();
        formData.append('pkbFile', payload.pkbFile);

        return this.api
            .post<any>(`institutions/${institution}/upload-draft-pkb-file-jkp`, formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public approveSignatureJkpFile(institution: string, payload: ApproveSignPkb): Observable<Institution> {
        const formData = new FormData();
        formData.append('jkpPkbDate', payload.jkpPkbDate);
        formData.append('jkpPkbNumber', payload.jkpPkbNumber);
        formData.append('draftPkbFile', payload.draftPkbFile);

        return this.api
            .post<any>(`institutions/${institution}/approve-draft-pkb-file-jkp`, formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public revokeJkpInstitution(institution: string, payload: any): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/revoke-jkp`, payload)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public unRevokeJkpInstitution(institution: string): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/unrevoke-jkp`, {})
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public addProgramInstitution(institution: string, payload: AddProgramJkp): Observable<InstitutionProgram> {
        return this.api
            .post<any>(`institutions/${institution}/institution-programs`, payload)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(InstitutionProgram));
    }

    public deleteProgramInstitution(institution: string, institutionProgram: string): Observable<InstitutionProgram> {
        return this.api
            .delete<any>(`institutions/${institution}/institution-programs/${institutionProgram}`)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(InstitutionProgram));
    }

    public fetchProfileByNacoUser(nacoUser: string): Observable<User> {
        return this.api
            .get<any>(`naco-users/${nacoUser}/profile`)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(User));
    }

    public uploadJkpDocument(document: string, payload: DocumentJkp): Observable<Document> {
        const formData = new FormData();
        formData.append('file', payload.file);

        return this.api
            .post<any>(`jkp-documents/${document}`, formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Document));
    }

    public verifyJkpDocument(document: string): Observable<Document> {
        return this.api
            .patch<any>(`jkp-documents/${document}/verify`, {})
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Document));
    }

    public rejectJkpDocument(document: string, payload: any): Observable<Document> {
        return this.api
            .patch<any>(`jkp-documents/${document}/reject`, payload)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Document));
    }

    public updateJkpInstitution(institution: string, payload: UpdateJkpInstitution): Observable<Institution> {
        return this.api
            .patch<any>(`institutions/${institution}/update-jkp`, payload)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }

    public fetchBanks(): Observable<Bank[]> {
        return this.api.get<any>(`banks`).pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(Bank));
    }

    public createDraftPkbDocument(institution: string, payload: any): Observable<Institution> {
        return this.api
            .post<any>(`institutions/${institution}/create-draft-pkb-file-jkp`, payload)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Institution));
    }
}



// WEBPACK FOOTER //
// ./src/lemsar/institution/domains/services/jkp-institution.service.ts